import { FacebookAggregate, FacebookMetric, GoogleAggregate, GoogleMetric, SitewideDataPoint, SitewideMetric } from "services/CompareService";

export const getFacebookMetricValue = (point: FacebookAggregate, metric: FacebookMetric): number => {
  const parseValueSafely = (value: string | undefined): number => {
    if (!value || value === '') return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  switch (metric) {
    case "spend":
      return parseValueSafely(point.spend);
    case "roas":
      return parseValueSafely(point.roas);
    case "cpa":
      return parseValueSafely(point.cpa);
    case "aov":
      return parseValueSafely(point.aov);
    case "cpc":
      return parseValueSafely(point.cpc);
    case "cpm":
      return parseValueSafely(point.cpm);
    case "ctr":
      return parseValueSafely(point.ctr);
    case "cvr":
      return parseValueSafely(point.cvr);
    case "impressions":
      return parseValueSafely(point.impressions);
    case "clicks":
      return parseValueSafely(point.clicks);
    case "purchases":
      return parseValueSafely(point.purchases);
    case "purchase_conversion_value":
      return parseValueSafely(point.purchase_conversion_value);
    default:
      return 0;
  }
};


export const getSitewideMetricValue = (point: SitewideDataPoint, metric: SitewideMetric): number => {
  const parseValueSafely = (value: string | undefined): number => {
    if (!value || value === '') return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : Number(parsed.toFixed(2));
  };

  switch (metric) {
    case "spend":
      return parseValueSafely(point.total_spend);
    case "mer":
      return parseValueSafely(point.mer);
    case "amer":
      return parseValueSafely(point.amer);
    case "cac":
      return parseValueSafely(point.cac);
    case "aov":
      return parseValueSafely(point.aov);
    default:
      return 0;
  }
};

export const generateFacebookChartSeries = (
  merchantData: FacebookAggregate[],
  benchmarkData: FacebookAggregate[],
  selectedMetric: FacebookMetric,
  selectedRegion?: string
) => {
  if (!merchantData.length || !benchmarkData.length) return [];

  const benchmarkName = selectedRegion ? `${selectedRegion} Average` : "All Regions Average";

  return [
    {
      name: "Your Facebook Performance",
      type: "line",
      metric: selectedMetric,
      data: merchantData.map((point) => ({
        x: new Date(point.date).getTime(),
        y: getFacebookMetricValue(point, selectedMetric),
      })),
    },
    {
      name: benchmarkName,
      type: "line",
      metric: selectedMetric,
      data: benchmarkData.map((point) => ({
        x: new Date(point.date).getTime(),
        y: getFacebookMetricValue(point, selectedMetric),
      })),
    },
  ];
};

export const getGoogleMetricValue = (point: GoogleAggregate, metric: GoogleMetric): number => {
  const parseValueSafely = (value: string | undefined): number => {
    if (!value || value === '') return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  switch (metric) {
    case "spend":
      return parseValueSafely(point.spend);
    case "purchase_conversion_value":
      return parseValueSafely(point.purchase_conversion_value);
    case "roas":
      return parseValueSafely(point.roas);
    case "cpa":
      return parseValueSafely(point.cpa);
    case "aov":
      return parseValueSafely(point.aov);
    case "cpc":
      return parseValueSafely(point.cpc);
    case "ctr":
      return parseValueSafely(point.ctr);
    case "cvr":
      return parseValueSafely(point.cvr);
    case "impressions":
      return parseValueSafely(point.impressions);
    case "clicks":
      return parseValueSafely(point.clicks);
    case "conversions":
      return parseValueSafely(point.conversions);
    default:
      return 0;
  }
};

export const generateGoogleChartSeries = (
  merchantData: GoogleAggregate[],
  benchmarkData: GoogleAggregate[],
  selectedMetric: GoogleMetric,
  selectedRegion?: string
) => {
  if (!merchantData.length || !benchmarkData.length) return [];

  const benchmarkName = selectedRegion ? `${selectedRegion} Average` : "All Regions Average";

  return [
    {
      name: "Your Google Ads Performance",
      type: "line",
      metric: selectedMetric,
      data: merchantData.map((point) => ({
        x: new Date(point.date).getTime(),
        y: getGoogleMetricValue(point, selectedMetric),
      })),
    },
    {
      name: benchmarkName,
      type: "line",
      metric: selectedMetric,
      data: benchmarkData.map((point) => ({
        x: new Date(point.date).getTime(),
        y: getGoogleMetricValue(point, selectedMetric),
      })),
    },
  ];
};

export const generateSitewideChartSeries = (
  merchantData: SitewideDataPoint[],
  benchmarkData: SitewideDataPoint[],
  selectedMetric: SitewideMetric,
  selectedRegion?: string
) => {
  if (!merchantData.length || !benchmarkData.length) return [];

  const benchmarkName = selectedRegion ? `${selectedRegion} Average` : "All Regions Average";

  return [
    {
      name: "Your Performance",
      type: "line",
      metric: selectedMetric,
      data: merchantData.map((point) => ({
        x: new Date(point.date).getTime(),
        y: getSitewideMetricValue(point, selectedMetric),
      })),
    },
    {
      name: benchmarkName,
      type: "line",
      metric: selectedMetric,
      data: benchmarkData.map((point) => ({
        x: new Date(point.date).getTime(),
        y: getSitewideMetricValue(point, selectedMetric),
      })),
    },
  ];
};
